import {
	faCircle,
	faDesktop,
	faExclamationTriangle,
	faMobile,
	faPhone,
	faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Dialog,
	DialogPanel,
	DialogTitle,
	Radio,
	RadioGroup,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import type { RingCentralUserDevice } from '@supply-mover-2-0/core';
import { RingCentralQueryService } from '@supply-mover-2-0/core';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Button } from '../forms/Button';
import { Checkbox } from '../forms/Checkbox';
import { Input } from '../forms/Input';

export interface RingCentralDialResult {
	dialMethod: 'callout' | 'ringout';
	// For CallOut
	deviceId?: string;
	deviceName?: string;
	// For RingOut
	ringOutPhoneNumber?: string;
	remember: boolean;
}

export interface RingCentralDialModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSelect: (result: RingCentralDialResult) => void;
	initialDialMethod?: 'callout' | 'ringout';
	initialRingOutPhone?: string;
}

export function RingCentralDialModal({
	isOpen,
	onClose,
	onSelect,
	initialDialMethod = 'callout',
	initialRingOutPhone = '',
}: RingCentralDialModalProps): React.ReactElement {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// Dial method state
	const [dialMethod, setDialMethod] = useState<'callout' | 'ringout'>(
		initialDialMethod,
	);

	// CallOut state
	const [isLoadingDevices, setIsLoadingDevices] = useState(false);
	const [devices, setDevices] = useState<Array<RingCentralUserDevice>>([]);
	const [selectedDevice, setSelectedDevice] =
		useState<RingCentralUserDevice | null>(null);
	const [deviceError, setDeviceError] = useState<string | null>(null);

	// RingOut state
	const [ringOutPhone, setRingOutPhone] = useState(initialRingOutPhone);
	const [phoneError, setPhoneError] = useState<string | null>(null);

	// Shared state
	const [rememberChoice, setRememberChoice] = useState(false);

	// Load devices when modal opens or when switching to CallOut
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		if (dialMethod !== 'callout') {
			return;
		}

		if (devices.length > 0) {
			return;
		}

		const loadDevices = async (): Promise<void> => {
			setIsLoadingDevices(true);
			setDeviceError(null);

			try {
				const userDevices = await RingCentralQueryService.getUserDevices();
				// Filter to only show devices that support CallOut
				const callOutDevices = userDevices.filter((d) => d.supportsCallOut);
				setDevices(callOutDevices);

				// Auto-select first device
				const firstDevice = callOutDevices[0];
				if (firstDevice != null) {
					setSelectedDevice(firstDevice);
				}
			} catch (err) {
				console.error('Failed to load devices:', err);
				setDeviceError(t('Failed to load devices. Please try again.'));
			} finally {
				setIsLoadingDevices(false);
			}
		};

		void loadDevices();
	}, [isOpen, dialMethod, devices.length, t]);

	// Reset state when modal closes
	useEffect(() => {
		if (!isOpen) {
			setDevices([]);
			setSelectedDevice(null);
			setDeviceError(null);
			setPhoneError(null);
		}
	}, [isOpen]);

	const validatePhoneNumber = useCallback(
		(phone: string): boolean => {
			const digits = phone.replace(/\D/g, '');

			if (digits.length < 10) {
				setPhoneError(
					t('Please enter a valid phone number with at least 10 digits'),
				);

				return false;
			}

			setPhoneError(null);

			return true;
		},
		[t],
	);

	const handleConfirm = useCallback((): void => {
		if (dialMethod === 'callout') {
			if (selectedDevice == null) {
				return;
			}

			onSelect({
				dialMethod: 'callout',
				deviceId: selectedDevice.deviceId,
				deviceName: selectedDevice.name,
				remember: rememberChoice,
			});
		} else {
			if (!validatePhoneNumber(ringOutPhone)) {
				return;
			}

			// Clean the phone number
			const cleanedPhone = ringOutPhone.startsWith('+')
				? `+${ringOutPhone.slice(1).replace(/\D/g, '')}`
				: ringOutPhone.replace(/\D/g, '');

			onSelect({
				dialMethod: 'ringout',
				ringOutPhoneNumber: cleanedPhone,
				remember: rememberChoice,
			});
		}
	}, [
		dialMethod,
		selectedDevice,
		ringOutPhone,
		rememberChoice,
		validatePhoneNumber,
		onSelect,
	]);

	const handlePhoneChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			setRingOutPhone(e.target.value);

			if (phoneError != null) {
				setPhoneError(null);
			}
		},
		[phoneError],
	);

	const getDeviceIcon = useCallback(
		(device: RingCentralUserDevice): React.ReactNode => {
			if (device.type === 'SoftPhone' || device.type === 'WebRTC') {
				return (
					<FontAwesomeIcon
						icon={faDesktop}
						className={colors.classes.textSecondary}
					/>
				);
			}

			if (device.type === 'Mobile') {
				return (
					<FontAwesomeIcon
						icon={faMobile}
						className={colors.classes.textSecondary}
					/>
				);
			}

			return (
				<FontAwesomeIcon
					icon={faPhone}
					className={colors.classes.textSecondary}
				/>
			);
		},
		[colors.classes.textSecondary],
	);

	const getStatusColor = useCallback(
		(status: string): string => {
			if (status === 'Online') {
				return 'text-green-500';
			}

			if (status === 'Offline') {
				return 'text-gray-400';
			}

			return colors.classes.textMuted;
		},
		[colors.classes.textMuted],
	);

	const canConfirm =
		dialMethod === 'callout'
			? selectedDevice != null
			: ringOutPhone.trim().length > 0;

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-50" onClose={onClose}>
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-black bg-opacity-25" />
				</TransitionChild>

				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 text-center">
						<TransitionChild
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<DialogPanel
								className={`w-full max-w-lg transform rounded-lg ${colors.classes.bgPrimary} text-left align-middle shadow-xl transition-all`}
							>
								{/* Header */}
								<div
									className={`px-6 py-4 border-b ${colors.classes.borderDefault}`}
								>
									<DialogTitle
										as="h3"
										className={`text-lg font-medium leading-6 ${colors.classes.textPrimary}`}
									>
										{t('Make a Call')}
									</DialogTitle>
								</div>

								{/* Content */}
								<div className="px-6 py-4 space-y-6">
									{/* Dial Method Selection */}
									<div>
										<label
											className={`block text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
										>
											{t('Call Method')}
										</label>
										<RadioGroup
											value={dialMethod}
											onChange={setDialMethod}
											className="flex gap-4"
										>
											<Radio
												value="callout"
												className={({ checked }) =>
													`flex-1 cursor-pointer rounded-lg px-4 py-3 border ${
														checked
															? `border-2 ${colors.classes.borderPrimary}`
															: colors.classes.borderDefault
													} ${colors.classes.bgSecondary}`
												}
											>
												{({ checked }) => (
													<div className="flex items-center justify-between">
														<div>
															<p
																className={`font-medium ${colors.classes.textPrimary}`}
															>
																{t('CallOut')}
															</p>
															<p
																className={`text-xs ${colors.classes.textMuted}`}
															>
																{t('Call from your softphone app')}
															</p>
														</div>
														{checked && (
															<span style={{ color: colors.values.primary }}>
																{'✓'}
															</span>
														)}
													</div>
												)}
											</Radio>

											<Radio
												value="ringout"
												className={({ checked }) =>
													`flex-1 cursor-pointer rounded-lg px-4 py-3 border ${
														checked
															? `border-2 ${colors.classes.borderPrimary}`
															: colors.classes.borderDefault
													} ${colors.classes.bgSecondary}`
												}
											>
												{({ checked }) => (
													<div className="flex items-center justify-between">
														<div>
															<p
																className={`font-medium ${colors.classes.textPrimary}`}
															>
																{t('RingOut')}
															</p>
															<p
																className={`text-xs ${colors.classes.textMuted}`}
															>
																{t('Ring your phone, then connect')}
															</p>
														</div>
														{checked && (
															<span style={{ color: colors.values.primary }}>
																{'✓'}
															</span>
														)}
													</div>
												)}
											</Radio>
										</RadioGroup>
									</div>

									{/* CallOut: Device Selection */}
									{dialMethod === 'callout' && (
										<div>
											<label
												className={`block text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
											>
												{t('Select Device')}
											</label>

											{isLoadingDevices ? (
												<div className="flex flex-col items-center justify-center py-6">
													<FontAwesomeIcon
														icon={faSpinner}
														className="animate-spin text-2xl mb-3"
														style={{ color: colors.values.primary }}
													/>
													<p className={colors.classes.textSecondary}>
														{t('Loading devices...')}
													</p>
												</div>
											) : deviceError != null ? (
												<div className="flex flex-col items-center justify-center py-6">
													<FontAwesomeIcon
														icon={faExclamationTriangle}
														className="text-2xl mb-3 text-red-500"
													/>
													<p className="text-red-500">{deviceError}</p>
												</div>
											) : devices.length === 0 ? (
												<div className="flex flex-col items-center justify-center py-6">
													<FontAwesomeIcon
														icon={faPhone}
														className={`text-2xl mb-3 ${colors.classes.textMuted}`}
													/>
													<p className={colors.classes.textSecondary}>
														{t('No devices found')}
													</p>
													<p
														className={`text-sm mt-2 ${colors.classes.textMuted}`}
													>
														{t(
															'Please ensure you have RingCentral Phone app installed and signed in.',
														)}
													</p>
												</div>
											) : (
												<RadioGroup
													value={selectedDevice}
													onChange={setSelectedDevice}
													className="space-y-2 max-h-48 overflow-y-auto"
												>
													{devices.map((device) => (
														<Radio
															key={device.deviceId}
															value={device}
															className={({ checked, focus }) =>
																`relative flex cursor-pointer rounded-lg px-4 py-3 border ${
																	checked
																		? `border-2 ${colors.classes.borderPrimary}`
																		: colors.classes.borderDefault
																} ${
																	focus ? 'ring-2 ring-offset-2' : ''
																} ${colors.classes.bgSecondary}`
															}
														>
															{({ checked }) => (
																<div className="flex w-full items-center justify-between">
																	<div className="flex items-center gap-3">
																		<div className="w-8 h-8 flex items-center justify-center">
																			{getDeviceIcon(device)}
																		</div>
																		<div>
																			<p
																				className={`font-medium ${colors.classes.textPrimary}`}
																			>
																				{t(device.name)}
																			</p>
																			<div className="flex items-center gap-2 text-xs">
																				<span
																					className={colors.classes.textMuted}
																				>
																					{t(device.type)}
																				</span>
																				<span
																					className={colors.classes.textMuted}
																				>
																					{'•'}
																				</span>
																				<span
																					className={`flex items-center gap-1 ${getStatusColor(device.status)}`}
																				>
																					<FontAwesomeIcon
																						icon={faCircle}
																						className="text-[6px]"
																					/>
																					{t(device.status)}
																				</span>
																				{device.phoneNumber != null && (
																					<>
																						<span
																							className={
																								colors.classes.textMuted
																							}
																						>
																							{'•'}
																						</span>
																						<span
																							className={
																								colors.classes.textMuted
																							}
																						>
																							{device.phoneNumber}
																						</span>
																					</>
																				)}
																			</div>
																		</div>
																	</div>
																	{checked && (
																		<div
																			className="shrink-0 text-lg"
																			style={{
																				color: colors.values.primary,
																			}}
																		>
																			{'✓'}
																		</div>
																	)}
																</div>
															)}
														</Radio>
													))}
												</RadioGroup>
											)}
										</div>
									)}

									{/* RingOut: Phone Number Input */}
									{dialMethod === 'ringout' && (
										<div>
											<label
												className={`block text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
											>
												{t('Your Phone Number')}
											</label>
											<p
												className={`text-sm mb-3 ${colors.classes.textSecondary}`}
											>
												{t(
													'Enter the phone number where you want to receive the call. RingCentral will call you first, then connect you to the destination.',
												)}
											</p>
											<Input
												type="tel"
												value={ringOutPhone}
												onChange={handlePhoneChange}
												placeholder={t('(555) 123-4567')}
												error={phoneError != null}
												autoFocus
											/>
											{phoneError != null && (
												<p className="text-sm text-red-500 mt-1">
													{phoneError}
												</p>
											)}
										</div>
									)}

									{/* Remember Choice */}
									<div className="flex items-center gap-2">
										<Checkbox
											checked={rememberChoice}
											onChange={setRememberChoice}
											label={t('Remember my choices')}
										/>
									</div>

									<p className={`text-xs ${colors.classes.textMuted} italic`}>
										{t(
											'You can reset your choices in your Profile settings under Call Preferences.',
										)}
									</p>
								</div>

								{/* Footer */}
								<div
									className={`flex justify-end gap-2 px-6 py-4 border-t ${colors.classes.borderDefault}`}
								>
									<Button variant="outline" onClick={onClose}>
										{t('Cancel')}
									</Button>
									<Button
										variant="primary"
										onClick={handleConfirm}
										disabled={!canConfirm || isLoadingDevices}
									>
										{t('Make Call')}
									</Button>
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
